import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { PageHeader, Card, Btn, Alert, colors, spacing, typography, radius, shadows } from '../design-system';

// ── Constants ──────────────────────────────────────────────────────────────────

const EVENT_TYPES = {
  surveillance: { label: 'Surveillance',   color: '#0ea5e9', bg: '#e0f2fe' },
  renewal:      { label: 'Renewal Due',    color: '#f59e0b', bg: '#fef3c7' },
  expiry:       { label: 'Cert Expiry',    color: '#ef4444', bg: '#fee2e2' },
  remote_audit: { label: 'Remote Audit',   color: '#8b5cf6', bg: '#ede9fe' },
  capa:         { label: 'CAPA Deadline',  color: '#f97316', bg: '#ffedd5' },
};

const DAYS    = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Helpers ────────────────────────────────────────────────────────────────────

function startOfMonth(year, month) {
  return new Date(year, month, 1);
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// ISO Mon=0 offset
function monthStartOffset(year, month) {
  const day = new Date(year, month, 1).getDay();
  return (day + 6) % 7;
}

function toDateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// ── Event Dot ──────────────────────────────────────────────────────────────────

function EventPill({ event, onClick }) {
  const cfg = EVENT_TYPES[event.type] || { label: event.type, color: '#64748b', bg: '#f1f5f9' };
  return (
    <div
      onClick={e => { e.stopPropagation(); onClick(event); }}
      title={event.title}
      style={{
        background: cfg.bg,
        color: cfg.color,
        borderLeft: `2px solid ${cfg.color}`,
        borderRadius: '0 2px 2px 0',
        fontSize: '10px',
        fontWeight: 600,
        padding: '1px 5px',
        marginBottom: 1,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1.6,
        letterSpacing: '0.01em',
      }}
    >
      {event.title}
    </div>
  );
}

// ── Day Cell ───────────────────────────────────────────────────────────────────

function DayCell({ day, isToday, isCurrentMonth, events, onEventClick }) {
  const [hovered, setHovered] = useState(false);
  const shown  = events.slice(0, 3);
  const overflow = events.length - 3;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        minHeight: 90,
        padding: '6px 4px 4px',
        background: isToday ? '#eff6ff' : hovered ? '#f8fafc' : colors.surface,
        border: `1px solid ${isToday ? '#bfdbfe' : colors.border}`,
        transition: 'background 100ms',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{
        fontSize: typography.fontSizes.sm,
        fontWeight: isToday ? typography.fontWeights.bold : typography.fontWeights.medium,
        color: isCurrentMonth ? (isToday ? '#1d4ed8' : colors.textPrimary) : colors.textMuted,
        marginBottom: 4,
        lineHeight: 1,
      }}>
        {day}
        {isToday && (
          <span style={{
            display: 'inline-block', marginLeft: 4, width: 5, height: 5,
            borderRadius: '50%', background: '#1d4ed8', verticalAlign: 'middle',
          }} />
        )}
      </div>
      <div>
        {shown.map(ev => (
          <EventPill key={ev.id} event={ev} onClick={onEventClick} />
        ))}
        {overflow > 0 && (
          <div style={{ fontSize: '10px', color: colors.textMuted, paddingLeft: 5, fontWeight: 600 }}>
            +{overflow} more
          </div>
        )}
      </div>
    </div>
  );
}

// ── Event Detail Panel ─────────────────────────────────────────────────────────

function EventPanel({ event, onClose }) {
  const navigate = useNavigate();
  if (!event) return null;
  const cfg = EVENT_TYPES[event.type] || { label: event.type, color: '#64748b', bg: '#f1f5f9' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: colors.surface,
          borderRadius: radius.lg,
          width: 380,
          boxShadow: shadows.xl || '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div style={{ height: 4, background: cfg.color }} />
        <div style={{ padding: spacing.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.md }}>
            <div>
              <div style={{
                fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: cfg.color, marginBottom: 4,
              }}>
                {cfg.label}
              </div>
              <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, lineHeight: 1.3 }}>
                {event.title}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: typography.fontSizes.sm }}>
            <Row label="Date"    value={event.date} />
            {event.time    && <Row label="Time"    value={event.time} />}
            {event.company && <Row label="Organisation" value={event.company} />}
            {event.status  && <Row label="Status"  value={event.status.replace(/_/g, ' ')} />}
          </div>

          {event.source_path && (
            <div style={{ marginTop: spacing.md }}>
              <Btn variant="primary" size="sm" onClick={() => { navigate(event.source_path); onClose(); }}>
                View Details
              </Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: colors.textMuted, minWidth: 90, flexShrink: 0 }}>{label}</span>
      <span style={{ color: colors.textPrimary, fontWeight: typography.fontWeights.medium, textTransform: 'capitalize' }}>{value}</span>
    </div>
  );
}

// ── Legend ─────────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
      {Object.entries(EVENT_TYPES).map(([key, cfg]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: typography.fontSizes.xs, color: colors.textSecondary }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color }} />
          {cfg.label}
        </div>
      ))}
    </div>
  );
}

// ── List View ──────────────────────────────────────────────────────────────────

function ListView({ events, onEventClick }) {
  const sorted = [...events].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  if (!sorted.length) return (
    <div style={{ textAlign: 'center', padding: `${spacing.xl} 0`, color: colors.textMuted, fontSize: typography.fontSizes.md }}>
      No events in this period.
    </div>
  );

  let lastDate = null;
  return (
    <div>
      {sorted.map(ev => {
        const cfg = EVENT_TYPES[ev.type] || { label: ev.type, color: '#64748b' };
        const showDate = ev.date !== lastDate;
        lastDate = ev.date;
        return (
          <div key={ev.id}>
            {showDate && (
              <div style={{
                fontSize: typography.fontSizes.xs, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: colors.textMuted, padding: `${spacing.md} 0 ${spacing.xs}`,
                borderBottom: `1px solid ${colors.border}`, marginBottom: spacing.sm,
              }}>
                {new Date(ev.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
            <div
              onClick={() => onEventClick(ev)}
              style={{
                display: 'flex', alignItems: 'center', gap: spacing.md,
                padding: `10px ${spacing.md}`,
                borderRadius: radius.md,
                cursor: 'pointer',
                marginBottom: 2,
                transition: 'background 100ms',
              }}
              onMouseEnter={e => e.currentTarget.style.background = colors.background}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: 3, height: 36, borderRadius: 2, background: cfg.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.medium, color: colors.textPrimary }}>{ev.title}</div>
                <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
                  {cfg.label}{ev.company ? ` · ${ev.company}` : ''}
                </div>
              </div>
              {ev.time && (
                <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>{ev.time}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AuditCalendar() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [view,  setView]  = useState('month'); // 'month' | 'list'
  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const start = new Date(year, month - 1, 1).toISOString();
      const end   = new Date(year, month + 2, 0).toISOString();
      const res = await axiosClient.get(`/calendar/events?start=${start}&end=${end}`);
      setEvents(res.data);
    } catch {
      setError('Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goToday   = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const filtered = filter === 'all' ? events : events.filter(e => e.type === filter);

  // Build date → events map
  const eventMap = {};
  filtered.forEach(ev => {
    if (!ev.date) return;
    if (!eventMap[ev.date]) eventMap[ev.date] = [];
    eventMap[ev.date].push(ev);
  });

  // Build calendar grid
  const offset   = monthStartOffset(year, month);
  const numDays  = daysInMonth(year, month);
  const prevDays = daysInMonth(year, month - 1);
  const cells    = [];

  for (let i = 0; i < offset; i++) {
    cells.push({ day: prevDays - offset + i + 1, current: false, date: toDateKey(year, month - 1, prevDays - offset + i + 1) });
  }
  for (let d = 1; d <= numDays; d++) {
    cells.push({ day: d, current: true, date: toDateKey(year, month, d) });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, current: false, date: toDateKey(year, month + 1, d) });
  }

  const todayKey = toDateKey(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="Audit Planning Calendar"
        subtitle="Surveillance visits, renewals, certificate expiries, and remote audits"
      />

      {error && <Alert type="error" message={error} />}

      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg, flexWrap: 'wrap', gap: spacing.sm }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <Btn variant="ghost" size="sm" onClick={prevMonth}>&#8592;</Btn>
          <div style={{ fontSize: typography.fontSizes.xl, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, minWidth: 200, textAlign: 'center' }}>
            {MONTHS[month]} {year}
          </div>
          <Btn variant="ghost" size="sm" onClick={nextMonth}>&#8594;</Btn>
          <Btn variant="secondary" size="sm" onClick={goToday}>Today</Btn>
        </div>

        <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Type filter */}
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{
              fontSize: typography.fontSizes.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              padding: '6px 10px',
              background: colors.surface,
              color: colors.textPrimary,
              cursor: 'pointer',
            }}
          >
            <option value="all">All types</option>
            {Object.entries(EVENT_TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          {/* View toggle */}
          <div style={{ display: 'flex', background: colors.background, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
            {[['month', 'Month'], ['list', 'List']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  background: view === v ? colors.primary : 'transparent',
                  color: view === v ? '#fff' : colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: typography.fontSizes.sm,
                  fontWeight: view === v ? typography.fontWeights.semibold : typography.fontWeights.medium,
                  transition: 'all 120ms',
                }}
              >
                {l}
              </button>
            ))}
          </div>

          <Btn variant="ghost" size="sm" onClick={load} disabled={loading}>Refresh</Btn>
        </div>
      </div>

      <Legend />
      <div style={{ marginTop: spacing.md }} />

      {/* Calendar grid */}
      {view === 'month' && (
        <Card padding="none">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${colors.border}` }}>
            {DAYS.map(d => (
              <div key={d} style={{
                padding: `${spacing.sm} ${spacing.xs}`,
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: colors.textMuted,
                borderRight: `1px solid ${colors.border}`,
              }}>
                {d}
              </div>
            ))}
          </div>
          {loading ? (
            <div style={{ padding: spacing.xl, textAlign: 'center', color: colors.textMuted }}>Loading events…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {cells.map((cell, i) => (
                <DayCell
                  key={i}
                  day={cell.day}
                  isToday={cell.date === todayKey}
                  isCurrentMonth={cell.current}
                  events={eventMap[cell.date] || []}
                  onEventClick={setSelected}
                />
              ))}
            </div>
          )}
        </Card>
      )}

      {view === 'list' && (
        <Card>
          {loading
            ? <div style={{ textAlign: 'center', color: colors.textMuted, padding: spacing.lg }}>Loading…</div>
            : <ListView events={filtered} onEventClick={setSelected} />
          }
        </Card>
      )}

      <EventPanel event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
